import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SleepSignalsGuideDownload from '@/pages/guia-senales-sueno/components/SleepSignalsGuideDownload';
import { programs } from '@/mocks/neurobaby';

const sleepSigns = [
  {
    id: 1,
    title: 'Bostezos frecuentes',
    icon: 'ri-sun-cloudy-line',
    what: 'Tu bebé empieza a bostezar una y otra vez, incluso después de haber despertado hace poco.',
    science: 'El bostezo es una señal directa de la homeostasis del sueño: el cerebro está detectando un desequilibrio entre la necesidad de descanso y la energía disponible. La corteza prefrontal —la última en madurar— es la primera en "apagarse", provocando estos bostezos automáticos.',
    action: 'Esta es tu ventana de oro. En los próximos 3-5 minutos, empezá tu ritual de sueño. Si esperás, el cuerpo liberará cortisol para "mantenerse despierto" y el bebé se hará más difícil de dormir.',
  },
  {
    id: 2,
    title: 'Frotarse los ojos u orejas',
    icon: 'ri-eye-close-line',
    what: 'Se frota los ojitos con los puños, tira de las orejas o se toca la cara repetidamente.',
    science: 'Señal de fatiga ocular y sobrecarga sensorial. El sistema nervioso parasimpático se activa como respuesta al cansancio. El nervio vago —el "gran calibrador" del relax— comienza a modular la frecuencia cardíaca hacia la baja. El frotamiento es una respuesta instintiva para estimular la producción de lágrimas y lubricar ojos secos por el esfuerzo de mantenerse despierto.',
    action: 'Actuar en 3-5 minutos. Bajá la intensidad del ambiente. Apagá luces brillantes, bajá el volumen de la música o la TV. Tu bebé está pidiendo un entorno más oscuro y tranquilo. No lo ignores —este es el momento de transición.',
  },
  {
    id: 3,
    title: 'Perder interés en el entorno',
    icon: 'ri-indeterminate-circle-line',
    what: 'Dejó de mirar los juguetes, no responde cuando le hablás, parece "mirar al vacío".',
    science: 'El cerebro empieza a desconectarse. El sistema límbico —especialmente la amígdala y el hipocampo— comienza a desconectar progresivamente la atención consciente del entorno. Es un mecanismo de protección: el cerebro infantil prioriza la conservación de energía sobre el procesamiento sensorial. No es "aburrimiento", es neurobiología pura.',
    action: 'Ventana de 3-5 minutos. No intentés "despertarlo" con más estímulos. Eso solo genera sobrecarga. Lleválo a su espacio de sueño en silencio. La desconexión atencional es una señal de que su cerebro ya está "cambiando de marcha" hacia el descanso.',
  },
  {
    id: 4,
    title: 'Movimientos bruscos de piernas y brazos',
    icon: 'ri-armchair-line',
    what: 'Patea, estira los brazos de golpe, se arquea, como si estuviera "inquieto" o incómodo.',
    science: 'El sistema nervioso está en alerta máxima. La regulación autónoma inmadura del sistema nervioso genera descargas de energía residual cuando el cuerpo intenta pasar del estado de alerta al de reposo. Es como si el cerebro infantil "no supiera cómo apagarse" y enviara señales contradictorias. Estos movimientos son intentos de autocalmarse, aunque parezcan lo opuesto.',
    action: 'Actuar en 1-3 minutos. Ofrecé contención física suave: abrazo tipo canguro, mecerlo rítmicamente o sostener sus manos contra su pecho. La contención externa ayuda a su sistema nervioso a completar la transición que no puede hacer solo todavía.',
  },
  {
    id: 5,
    title: 'Quejidos o llanto sordo',
    icon: 'ri-emotion-sad-line',
    what: 'Empieza a gemir, a hacer sonidos quejumbrosos o llora de forma sorda, sin llegar a un llanto fuerte.',
    science: 'Última señal antes del llanto fuerte. El sistema está experimentando lo que los neurocientíficos llaman "disfagia del sueño" —una dificultad para iniciar el descanso porque el nivel de cortisol (hormona del estrés) ya superó el umbral. El colapso del sistema de regulación hace que el llanto sea "sordo" porque el bebé ya no tiene energía para un pataleo fuerte.',
    action: 'Acción inmediata. Acá no hay que perder tiempo. El bebé necesita ayuda activa para bajar el cortisol: contacto piel con piel, succión (teta o chupete), movimiento rítmico. No es "mimoso" —es neurobiología del apego calmando el sistema de alarma.',
  },
];

const breathingSteps = [
  { name: 'Inhalá', duration: 4000, instruction: 'Por la nariz, contando hasta 4. Dejá que el aire llene tu abdomen.' },
  { name: 'Sostené', duration: 2000, instruction: 'Retené el aire suavemente. Sin forzar. Tu bebé siente tu calma.' },
  { name: 'Exhalá', duration: 6000, instruction: 'Por la boca, lenta y completamente. Solitá la tensión de los hombros.' },
];

export default function GuiaSenalesSueno() {
  const [activeSign, setActiveSign] = useState(0);
  const { t } = useTranslation();
  const [breathStep, setBreathStep] = useState(0);
  const [breathActive, setBreathActive] = useState(false);
  const [breathProgress, setBreathProgress] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (breathActive) {
      const step = breathingSteps[breathStep];
      const startTime = Date.now();
      interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / step.duration) * 100, 100);
        setBreathProgress(progress);
        if (elapsed >= step.duration) {
          if (breathStep < breathingSteps.length - 1) {
            setBreathStep((prev) => prev + 1);
            setBreathProgress(0);
          } else {
            setBreathStep(0);
            setBreathProgress(0);
            setCycleCount((prev) => prev + 1);
          }
        }
      }, 50);
    }
    return () => clearInterval(interval);
  }, [breathActive, breathStep]);

  const handleBreathToggle = () => {
    if (breathActive) {
      setBreathActive(false);
      setBreathStep(0);
      setBreathProgress(0);
    } else {
      setBreathActive(true);
      setBreathStep(0);
      setBreathProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-sand/40">
        <div
          className="h-full bg-terracotta transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <Navbar />

      {/* Hero — ahora más compacto y apretado */}
      <section className="relative pt-20 md:pt-28 pb-8 md:pb-14 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-center mb-6 md:mb-10">
            <div className="w-full lg:w-1/2">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-block bg-[#AAB7A3] text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('senales.tag')}
                </span>
                <span className="inline-block bg-terracotta text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('senales.tag2')}
                </span>
                <span className="inline-block bg-sand text-[#3D2B1F] text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('common.basado_neurociencia')}
                </span>
              </div>
              <h1 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold text-[#8B6F5C] leading-tight mb-4">
                {t('senales.title1')}
                <br />
                <span className="italic">{t('senales.title2')}</span>
              </h1>
              <p className="font-sans text-sm md:text-base text-[#3D2B1F] leading-relaxed">
                Una guía simple basada en neurociencia para que sepas exactamente
                qué está pasando en el cuerpo de tu bebé cuando se pone "difícil".
              </p>
            </div>
            {/* Imagen entera en horizontal, al mismo tamaño que el texto */}
            <div className="w-full lg:w-1/2 flex items-center justify-center">
              <img
                src="https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/f48622a1-0ef1-4298-b728-b66953fecfc5_compressed_ChatGPT-Image-11-ago-2026-10_04_20-p.m..webp"
                alt="Mercedes presentando las 5 señales de sueño"
                className="w-full max-w-[270px] md:max-w-[336px] h-auto rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Las 5 Señales con Tabs — más compacta */}
      <section className="py-10 md:py-16 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-3">
              {t('senales.tabs_tag')}
            </span>
            <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#8B6F5C] leading-tight mb-3">
              {t('senales.tabs_title')}
            </h2>
            <p className="font-sans text-sm text-[#3D2B1F] max-w-2xl mx-auto">
              Cada señal tiene una explicación científica simple y una acción concreta.
              Navegá entre ellas para conocerlas en orden.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-6 md:mb-8 justify-center">
            {sleepSigns.map((sign, index) => (
              <button
                key={sign.id}
                onClick={() => setActiveSign(index)}
                className={`inline-flex items-center gap-2 font-sans text-sm font-semibold px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap ${
                  activeSign === index
                    ? 'bg-terracotta text-white shadow-sm'
                    : 'bg-white text-[#3D2B1F] hover:bg-sand/50 border border-sand/40'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 text-xs font-bold">
                  {sign.id}
                </span>
                {sign.title}
              </button>
            ))}
          </div>

          {/* Active Sign Content */}
          <div className="bg-white rounded-2xl p-5 md:p-8 border border-sand/40">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#AAB7A3]/15 text-[#AAB7A3]">
                <i className={`${sleepSigns[activeSign].icon} text-lg`} />
              </div>
              <div>
                <span className="font-sans text-xs font-semibold text-terracotta uppercase tracking-wider">
                  Señal {sleepSigns[activeSign].id} de 5
                </span>
                <h3 className="font-serif text-xl md:text-2xl font-bold text-[#8B6F5C] leading-tight">
                  {sleepSigns[activeSign].title}
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div className="md:col-span-1">
                <div className="bg-cream rounded-xl p-5 h-full">
                  <span className="inline-block font-sans text-xs font-semibold text-[#5C4A3A] uppercase tracking-wider mb-2">
                    {t('senales.que_pasa')}
                  </span>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed">
                    {sleepSigns[activeSign].what}
                  </p>
                </div>
              </div>
              <div className="md:col-span-1">
                <div className="bg-cream rounded-xl p-5 h-full">
                  <span className="inline-block font-sans text-xs font-semibold text-[#5C4A3A] uppercase tracking-wider mb-2">
                    {t('senales.ciencia_detras')}
                  </span>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed">
                    {sleepSigns[activeSign].science}
                  </p>
                </div>
              </div>
              <div className="md:col-span-1">
                <div className="bg-terracotta/10 rounded-xl p-5 h-full border border-terracotta/20">
                  <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-wider mb-2">
                    {t('senales.que_hacer')}
                  </span>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed font-medium">
                    {sleepSigns[activeSign].action}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation arrows */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-sand/30">
              <button
                onClick={() => setActiveSign((prev) => (prev > 0 ? prev - 1 : sleepSigns.length - 1))}
                className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
              >
                <i className="ri-arrow-left-line" />
                {t('senales.senal_anterior')}
              </button>
              <span className="font-sans text-xs text-[#5C4A3A]">
                {activeSign + 1} / {sleepSigns.length}
              </span>
              <button
                onClick={() => setActiveSign((prev) => (prev < sleepSigns.length - 1 ? prev + 1 : 0))}
                className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
              >
                {t('senales.senal_siguiente')}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tabla Resumen — más compacta */}
      <section className="py-10 md:py-16 px-6 md:px-10 bg-white">
        <div className="w-full max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#8B6F5C] leading-tight mb-2">
              {t('senales.tabla_title')}
            </h2>
            <p className="font-sans text-sm text-[#3D2B1F]">
              {t('senales.tabla_desc')}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#AAB7A3] text-white">
                  <th className="font-sans text-xs font-semibold text-left px-3 py-2 rounded-tl-xl">
                    {t('senales.tabla_senal')}
                  </th>
                  <th className="font-sans text-xs font-semibold text-left px-3 py-2">
                    {t('senales.tabla_que_mirar')}
                  </th>
                  <th className="font-sans text-xs font-semibold text-left px-3 py-2 rounded-tr-xl">
                    {t('senales.tabla_ventana')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sleepSigns.map((sign, index) => (
                  <tr
                    key={sign.id}
                    className={`${index % 2 === 0 ? 'bg-cream/50' : 'bg-white'} border-b border-sand/30`}
                  >
                    <td className="font-sans text-xs font-semibold text-[#3D2B1F] px-3 py-3">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#AAB7A3]/20 text-[#AAB7A3] text-xs font-bold">
                          {sign.id}
                        </span>
                        {sign.title}
                      </span>
                    </td>
                    <td className="font-sans text-xs text-[#3D2B1F] px-3 py-3 leading-relaxed">
                      {sign.what.split('.')[0]}.
                    </td>
                    <td className="font-sans text-xs font-medium text-terracotta px-3 py-3">
                      {sign.id <= 3 ? '3-5 minutos' : sign.id === 4 ? '1-3 minutos' : 'Inmediata'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Dr. Matthew Walker — más compacto */}
      <section className="py-10 md:py-16 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-sand/40">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
              <div className="w-full md:w-1/3 flex-shrink-0">
                <div className="w-full aspect-[4/5] rounded-xl overflow-hidden">
                  <img
                    src="https://storage.readdy-site.link/project_files/33950b86-7657-493b-8177-e5932d2de9a1/1d17a52d-b3b3-4d13-ac96-05d2ce6aba63_https___prod-bof-media.s3.eu-west-1.amazonaws.com_import_profiles_asset_01k9yaftag155qvdxpgv39hv7g_p.jpg?v=4101142b1a2d6eb4db8fe6085be8ef01"
                    alt="Dr. Matthew Walker"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="w-full md:w-2/3">
                <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-2">
                  {t('senales.ciencia_tag')}
                </span>
                <h2 className="font-serif text-xl md:text-2xl font-bold text-[#8B6F5C] leading-tight mb-2">
                  Dr. Matthew Walker
                </h2>
                <p className="font-sans text-xs text-[#5C4A3A] font-semibold mb-3">
                  Profesor de Neurociencia en UC Berkeley — Autor de "Why We Sleep"
                </p>
                <blockquote className="font-serif text-base md:text-lg text-[#3D2B1F] italic leading-relaxed mb-4 border-l-4 border-terracotta pl-4">
                  "El sueño es la base de todo aprendizaje, regulación emocional y salud física
                  en los primeros años de vida. No es un lujo, es una necesidad biológica
                  imperativa."
                </blockquote>
                <p className="font-sans text-xs text-[#3D2B1F] leading-relaxed mb-3">
                  El trabajo del Dr. Walker en el Center for Human Sleep Science de la Universidad
                  de California, Berkeley, demostró que el sueño infantil no es simplemente un
                  "descanso" —es un estado activo donde el cerebro consolida memoria, elimina toxinas
                  neurales y reorganiza las conexiones sinápticas formadas durante el día.
                </p>
                <p className="font-sans text-xs text-[#3D2B1F] leading-relaxed">
                  Sus investigaciones mostraron que <strong>interrumpir los ciclos de sueño
                  infantil</strong> —incluso por necesidades de alimentación mal sincronizadas—
                  impacta directamente en el desarrollo cognitivo y emocional. Por eso, leer las
                  señales de sueño y respetar la ventana de descanso es una inversión en el futuro
                  de tu bebé, no solo en su noche.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ejercicio de Respiración — más compacto */}
      <section className="py-10 md:py-16 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <span className="inline-block font-sans text-xs font-semibold text-cream uppercase tracking-[0.2em] mb-3">
            {t('senales.breath_tag')}
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-white leading-tight mb-2">
            {t('senales.breath_title')}
          </h2>
          <p className="font-sans text-sm text-cream max-w-xl mx-auto mb-8">
            {t('senales.breath_desc')}
          </p>

          {/* Breathing Circle */}
          <div className="relative w-52 h-52 mx-auto mb-8">
            <div
              className="absolute inset-0 rounded-full border-2 border-white/30 transition-all duration-300"
              style={{
                transform: breathActive
                  ? `scale(${breathStep === 0 ? 0.5 + breathProgress * 0.5 : breathStep === 1 ? 1 : breathStep === 2 ? 1 - breathProgress * 0.5 : 0.5})`
                  : 'scale(0.5)',
              }}
            />
            <div className="absolute inset-4 rounded-full bg-terracotta/20 flex items-center justify-center transition-all duration-300">
              <div className="text-center">
                <p className="font-serif text-2xl md:text-3xl font-bold text-white mb-1">
                  {breathingSteps[breathStep].name}
                </p>
                <p className="font-sans text-xs text-cream max-w-[160px] mx-auto leading-relaxed">
                  {breathingSteps[breathStep].instruction}
                </p>
              </div>
            </div>
            {/* Progress ring */}
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="46"
                fill="none"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth="2"
              />
              <circle
                cx="50"
                cy="50"
                r="46"
                fill="none"
                stroke="#C58D72"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray={`${breathProgress * 2.89} 289`}
                className="transition-all duration-100"
              />
            </svg>
          </div>

          {/* Cycle counter */}
          {cycleCount > 0 && (
            <p className="font-sans text-sm text-cream mb-4">
              Ciclos completados: <span className="text-terracotta font-semibold">{cycleCount}</span>
            </p>
          )}

          {/* Controls */}
          <button
            onClick={handleBreathToggle}
            className={`inline-flex items-center gap-3 px-6 py-3 rounded-full font-sans text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
              breathActive
                ? 'bg-white/10 text-white border border-white/30 hover:bg-white/20'
                : 'bg-terracotta text-white hover:bg-[#8B6F5C]'
            }`}
          >
            <i className={`${breathActive ? 'ri-pause-line' : 'ri-play-line'} text-lg`} />
            {breathActive ? 'Pausar' : 'Empezar ejercicio'}
          </button>

          {!breathActive && cycleCount === 0 && (
            <p className="font-sans text-xs text-cream mt-3">
              Inhalá 4 segundos · Sostené 2 segundos · Exhalá 6 segundos.
            </p>
          )}
        </div>
      </section>

      {/* Descarga gratuita — Guía de Señales de Sueño */}
      <section className="py-10 md:py-16 px-6 md:px-10 bg-white">
        <div className="w-full max-w-4xl mx-auto">
          <SleepSignalsGuideDownload />
        </div>
      </section>

      {/* CTA — Elegí tu programa */}
      <section className="py-10 md:py-16 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-2">
              {t('tutorial.next_step')}
            </span>
            <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#8B6F5C] leading-tight mb-3">
              {t('senales.cta_title')}
            </h2>
            <p className="font-sans text-sm md:text-base text-[#3D2B1F] max-w-2xl mx-auto">
              {t('senales.cta_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {programs.map((program) => (
              <div
                key={program.id}
                className="group bg-white rounded-2xl overflow-hidden border border-sand/40 transition-all duration-300 hover:border-terracotta/40 flex flex-col"
              >
                <div className="h-40 overflow-hidden relative">
                  <img
                    src={program.image}
                    alt={t(`programs.items.${program.id}.title`, program.title)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="font-sans text-xs font-semibold text-[#8B6F5C]">
                      {t(`programs.items.${program.id}.level`, program.level)}
                    </span>
                  </div>
                  {'badge' in program && (program as any).badge && (
                    <div className="absolute top-3 right-3 bg-terracotta text-white px-3 py-1 rounded-full">
                      <span className="font-sans text-xs font-semibold">{(program as any).badge}</span>
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <span className="font-sans text-xs text-[#5C4A3A] mb-1">
                    {t(`programs.items.${program.id}.subtitle`, program.subtitle)}
                  </span>
                  <h3 className="font-serif text-lg md:text-xl font-bold text-[#8B6F5C] mb-2 leading-tight">
                    {t(`programs.items.${program.id}.title`, program.title)}
                  </h3>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed mb-4 flex-grow">
                    {t(`programs.items.${program.id}.description`, program.description)}
                  </p>
                  <div className="flex items-center gap-1.5 mb-4 text-xs text-[#5C4A3A]">
                    <i className="ri-book-open-line text-xs" />
                    <span>{program.lessons} {t('common.guias')}</span>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-sand/30">
                    <div>
                      <span className="font-sans text-2xl font-bold text-[#8B6F5C]">${program.price}</span>
                      <span className="font-sans text-xs text-[#5C4A3A] ml-1">{t('common.usd')}</span>
                    </div>
                    <Link
                      to={program.id === 1 ? '/fundamentos-sueno' : program.id === 2 ? '/yoga-sueno-profundo' : '/programa-completo-neurobabys'}
                      className="inline-flex items-center bg-terracotta text-white px-5 py-2.5 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap"
                    >
                      {t('common.conocer_mas')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}